import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

const empty = { sku: "", name: "", category: "Paper", unit: "sheet", quantity: 0, min_qty: 0, cost: 0, supplier: "", location: "", notes: "" };

export const StockItemDialog = ({ item, meta, onClose, onSaved }) => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setF({ ...empty, ...item }); }, [item]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, quantity: Number(f.quantity), min_qty: Number(f.min_qty), cost: Number(f.cost) };
      if (item?.id) await api.put(`/stock/${item.id}`, body); else await api.post("/stock", body);
      toast.success(item?.id ? "Stock item updated" : "Stock item added"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="stock-item-dialog">
        <DialogHeader><DialogTitle>{item?.id ? `Edit ${item.sku}` : "Add Stock Item"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => set("sku", e.target.value.toUpperCase())} placeholder="Auto if blank" data-testid="stock-f-sku" /></div>
          <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="stock-f-category"><SelectValue /></SelectTrigger><SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label>Item Name *</Label><Input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="stock-f-name" /></div>
          <div><Label>Unit</Label><Select value={f.unit} onValueChange={(v) => set("unit", v)}><SelectTrigger data-testid="stock-f-unit"><SelectValue /></SelectTrigger><SelectContent>{meta.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Cost per unit (RM)</Label><Input type="number" step="0.01" min="0" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="stock-f-cost" /></div>
          <div><Label>{item?.id ? "Quantity (edit = adjustment)" : "Opening Quantity"}</Label><Input type="number" step="any" min="0" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="stock-f-qty" /></div>
          <div><Label>Minimum level (low-stock alert)</Label><Input type="number" step="any" min="0" value={f.min_qty} onChange={(e) => set("min_qty", e.target.value)} data-testid="stock-f-min" /></div>
          <div><Label>Supplier</Label><Input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="stock-f-supplier" /></div>
          <div><Label>Rack / Location</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="stock-f-location" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={busy} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="stock-f-save">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const StockMoveForm = ({ item, type, onSaved, compact = false }) => {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [job, setJob] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!qty || Number(qty) <= 0) return toast.error("Enter a quantity");
    setBusy(true);
    try {
      const { data } = await api.post(`/stock/${item.id}/move`, { type, quantity: Number(qty), note, job_number: job });
      toast.success(`${type === "in" ? "Stock In" : "Stock Out"} ${qty} ${item.unit} • balance ${data.item.quantity}`);
      setQty(""); setNote(""); setJob(""); onSaved(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid={`stock-move-form-${type}`}>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Quantity ({item.unit}) *</Label><Input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus className={compact ? "h-12 text-lg" : ""} data-testid="stock-move-qty" /></div>
        <div><Label>Job No. (optional)</Label><Input value={job} onChange={(e) => setJob(e.target.value.toUpperCase())} placeholder="RZF-…" className={compact ? "h-12" : ""} data-testid="stock-move-job" /></div>
      </div>
      <div><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={type === "in" ? "Supplier DO / PO no." : "Used for…"} data-testid="stock-move-note" /></div>
      <Button type="submit" disabled={busy} className={`w-full ${compact ? "h-12" : ""} text-white ${type === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="stock-move-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : type === "in" ? "Confirm Stock In" : "Confirm Stock Out"}
      </Button>
    </form>
  );
};

export const StockMoveDialog = ({ move, onClose, onSaved }) => (
  <Dialog open={!!move} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md" data-testid="stock-move-dialog">
      {move && <>
        <DialogHeader><DialogTitle>{move.type === "in" ? "Stock In" : "Stock Out"} — {move.item.name}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">Current: <span className="font-mono font-bold text-foreground">{move.item.quantity} {move.item.unit}</span> • {move.item.sku}</div>
        <StockMoveForm item={move.item} type={move.type} onSaved={onSaved} />
      </>}
    </DialogContent>
  </Dialog>
);

export const StockImportDialog = ({ open, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (!open) setPreview(null); }, [open]);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/stock/import/preview", fd); setPreview(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not read file"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/stock/import/commit", { rows: preview.rows }); toast.success(`Imported: ${data.created} new, ${data.updated} updated`); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="stock-import-dialog">
        <DialogHeader><DialogTitle>Import Stock from Excel / CSV</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Columns: sku, name, category, unit, quantity, min_qty, cost, supplier, location, notes. Rows with an existing SKU are updated; new SKUs are created (blank SKU = auto). Download the Template for the exact format.</p>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/50 text-sm">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} {preview ? "Choose another file" : "Choose .xlsx or .csv file"}
          <input ref={fileRef} type="file" hidden accept=".xlsx,.xls,.csv" onChange={pick} data-testid="stock-import-file" />
        </label>
        {preview && (
          <div className="space-y-2">
            <div className="flex gap-2 text-xs" data-testid="stock-import-summary"><Badge className="bg-emerald-600">{preview.create} new</Badge><Badge className="bg-cyan-600">{preview.update} update</Badge>{preview.errors > 0 && <Badge className="bg-rose-600">{preview.errors} error(s) — skipped</Badge>}</div>
            <div className="max-h-72 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0"><tr className="text-left"><th className="p-1.5">Row</th><th className="p-1.5">Action</th><th className="p-1.5">SKU</th><th className="p-1.5">Name</th><th className="p-1.5">Cat</th><th className="p-1.5 text-right">Qty</th><th className="p-1.5 text-right">Min</th><th className="p-1.5">Issue</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className={`border-t ${r.action === "error" ? "bg-rose-50" : ""}`} data-testid={`import-row-${r.row}`}>
                      <td className="p-1.5 font-mono">{r.row}</td>
                      <td className="p-1.5"><Badge variant="outline" className={r.action === "create" ? "text-emerald-700" : r.action === "update" ? "text-cyan-700" : "text-rose-700"}>{r.action}</Badge></td>
                      <td className="p-1.5 font-mono">{r.item.sku || <span className="text-muted-foreground">auto</span>}</td>
                      <td className="p-1.5">{r.item.name}</td><td className="p-1.5">{r.item.category}</td>
                      <td className="p-1.5 text-right font-mono">{r.current_qty != null && r.current_qty !== r.item.quantity ? <span className="text-muted-foreground line-through mr-1">{r.current_qty}</span> : null}{r.item.quantity}</td>
                      <td className="p-1.5 text-right font-mono">{r.item.min_qty}</td>
                      <td className="p-1.5 text-rose-600">{r.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={!preview || busy || preview.create + preview.update === 0} onClick={commit} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="stock-import-commit">Confirm Import{preview ? ` (${preview.create + preview.update})` : ""}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
